<template>
  <div v-if="!automatic && !item.prefillHidden" :id="item.uuid" :class="{'hide-after-action': item.hideAfterAction}" class="question">
    <div>
      <Tooltip v-if="hasImage" position="is-right" :label="$t('inspection-conduct.click-to-zoom-in')">
        <ImageAsset class="question__image" :photo="item" @click="showImage" />
      </Tooltip>
      <h2 class="whitespace-pre-line">
        <span v-if="item.required" class="text-red">* </span>
        <RichText v-if="item.params.rte_title" :content="title" />
        <span v-else>{{ title }}</span>
        <p v-if="hasFile" class="preview__question-file" @click="saveFile">
          <font-awesome-icon :icon="['far', 'download']" size="lg" />
          {{ $t('common.download-file') }}
        </p>
      </h2>

      <ResponseText
        v-if="item.response_type === ResponsesTypes.text && !isRTEEnabled"
        :key="item.uuid"
        :item="item"
        @update-response="updateTextResponse"
      />

      <RichTextResponse
        v-if="item.response_type === ResponsesTypes.text && isRTEEnabled"
        :key="item.uuid"
        :item="item"
        @update-response="updateHtmlResponse"
      />

      <ResponseNumber
        v-if="item.response_type === ResponsesTypes.number"
        :key="item.uuid"
        :item="item"
      />

      <ResponseCheckbox
        v-if="item.response_type === ResponsesTypes.checkbox"
        :key="item.uuid"
        :item="item"
      />

      <ResponseList
        v-if="item.response_type === ResponsesTypes.list"
        :key="item.uuid"
        :item="item"
        :response-sets="responseSets"
      />

      <ResponseDateTime
        v-if="item.response_type === ResponsesTypes.datetime"
        :key="item.uuid"
        :item="item"
      />

      <ResponseInstruction
        v-if="item.response_type === ResponsesTypes.instruction"
        :key="item.uuid"
        :item="item"
      />

      <ResponseAnnotation
        v-if="item.response_type === ResponsesTypes.annotation"
        :key="item.uuid"
        :item="item"
      />

      <ResponsePhoto
        v-if="item.response_type === ResponsesTypes.photo"
        :key="item.uuid"
        :item="item"
      />

      <ResponseSignature
        v-if="item.response_type === ResponsesTypes.signature"
        :key="item.uuid"
        :item="item"
      />

      <ResponseQrCode
        v-if="item.response_type === ResponsesTypes.qrCode"
        :key="item.uuid"
        :item="item"
      />

      <ResponseGeolocation v-if="item.response_type === ResponsesTypes.geoLocation" :key="item.uuid" />

      <ResponseEntityItemTypeOfResponse
        v-if="item.response_type === ResponsesTypes.entityItem && isEntityTypeOfResponseEnabled()"
        :key="item.uuid"
        :item="item"
      />

      <ResponseFile
        v-if="item.response_type === ResponsesTypes.file"
        :key="item.uuid"
        :item="item"
      />

      <div v-if="showErrors && hasError" class="error-text mt-2 text-red">
        {{ itemErrors.join(', ') }}
      </div>

    </div>
  </div>
</template>

<script>
  import ResponseText from '@/_legacy/views/ConductInspection/Responses/Text.vue';
  import RichTextResponse from '@/_legacy/views/ConductInspection/Responses/RichTextResponse.vue';
  import ResponseNumber from '@/_legacy/views/ConductInspection/Responses/Number.vue';
  import ResponseCheckbox from '@/_legacy/views/ConductInspection/Responses/Checkbox.vue';
  import ResponseList from '@/_legacy/views/ConductInspection/Responses/List.vue';
  import ResponseDateTime from '@/_legacy/views/ConductInspection/Responses/DateTime.vue';
  import ResponseInstruction from '@/_legacy/views/ConductInspection/Responses/Instruction.vue';
  import ResponseAnnotation from '@/_legacy/views/ConductInspection/Responses/Annotation.vue';
  import ResponsePhoto from '@/_legacy/views/ConductInspection/Responses/Photo.vue';
  import ResponseSignature from '@/_legacy/views/ConductInspection/Responses/Signature.vue';
  import ResponseEntityItem from '@/_legacy/views/ConductInspection/Responses/EntityItem.vue';
  import ResponseEntityItemTypeOfResponse from '@/_legacy/views/ConductInspection/Responses/EntityItemTypeOfResponse.vue';
  import ResponseFile from '@/_legacy/views/ConductInspection/Responses/File.vue';
  import { QrCode as ResponseQrCode } from '@/_legacy/views/ConductInspection/Responses/QrCode';
  import { Geolocation as ResponseGeolocation } from '@/_legacy/views/ConductInspection/Responses/Geolocation';
  import { ResponsesTypes } from '@/entities/checklist';
  import { ImageAsset } from '@/ui/components/asset/ImageAsset';
  import fileMixin from '@/_legacy/mixins/fileMixin';
  import { isEntityTypeOfResponseEnabled, isRTEEnabled } from '@/ui/helpers/featureHelper';
  import { Tooltip } from '@/ui/components/Tooltip';
  import RichText from '@/ui/components/richText/RichText.vue';
  import { useConductStore } from '@/stores/conduct/conduct';

  export default {
    components: {
      RichText,
      ResponseText,
      RichTextResponse,
      ResponseNumber,
      ResponseCheckbox,
      ResponseList,
      ResponseDateTime,
      ResponseInstruction,
      ResponseAnnotation,
      ResponsePhoto,
      ResponseSignature,
      ResponseQrCode,
      ResponseGeolocation,
      ResponseEntityItem,
      ResponseEntityItemTypeOfResponse,
      ResponseFile,
      ImageAsset,
      Tooltip,
    },
    mixins: [fileMixin],
    props: {
      item: {
        type: Object,
        required: true,
      },
      errors: {
        type: Object,
        required: true,
      },
      showErrors: {
        type: Boolean,
        required: true,
      },
      responseSets: {
        type: Array,
        required: true,
      },
    },
    setup() {
      const { isTranslationEnabled } = useConductStore();

      return { isTranslationEnabled };
    },
    data() {
      return {
        ResponsesTypes
      };
    },
    computed: {
      hasError() {
        return typeof (this.errors[this.item.uuid]) !== 'undefined';
      },
      itemErrors() {
        return this.errors[this.item.uuid];
      },
      automatic() {
        return this.item.params && this.item.params.automatic === true;
      },
      hasImage() {
        return !!this.item.image_id;
      },
      isRTEEnabled() {
        return isRTEEnabled() && this.item?.params?.show_rte_response;
      },
      title() {
        if (this.item.params?.rte_title) {
          return this.isTranslationEnabled && this.item.html_title_translated
            ? this.item.html_title_translated
            : this.item.html_title;
        }

        return this.isTranslationEnabled && this.item.title_translated
          ? this.item.title_translated
          : this.item.title;
      }
    },
    methods: {
      isEntityTypeOfResponseEnabled,
      showImage() {
        this.$store.commit('conduct/showImageModal', this.item);
      },
      updateTextResponse(uuid, response) {
        this.$store.commit('conduct/changeResponse', {
          item: uuid,
          response,
          responded: response.length > 0,
        });
      },
      updateHtmlResponse(uuid, htmlResponse) {
        this.$store.commit('conduct/changeResponse', {
          item: uuid,
          response: '',
          responded: htmlResponse.length > 0,
          rteResponse: true,
          htmlResponse,
        });
      },
    },
  };
</script>
